import * as React from "react";

import { SelectionItem } from "@replohq/lattice/blocks/selection/SelectionItem";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import {
  clearFeatureFlagOverrides,
  disableFeatureFlag,
  enableFeatureFlag,
  isFeatureEnabled,
} from "@v2/features/infra/featureFlags";
import { Search } from "lucide-react";
import { featureFlags } from "replo-utils/lib/featureFlags";
import { useForceUpdate } from "replo-utils/react/use-force-update";

export const FeatureFlagsTab: React.FC = () => {
  /**
   * Note (Noah, 2023-01-19): We have to do this rerender hack because we
   * don't have a hook to respond to changes in posthog feature flags
   */
  const rerender = useForceUpdate();
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredSortedFlags = React.useMemo(() => {
    return featureFlags.filter((flag) => {
      return flag.value.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [searchTerm]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-surface px-2.5 py-1.5">
        <div className="flex min-w-0 basis-64 grow flex-col gap-0.5">
          <span className="text-xs font-medium text-default">
            Reset to Defaults
          </span>
          <span className="text-xs text-muted">
            Clear all local overrides and restore server-side flag values.{" "}
            <a
              className="underline underline-offset-2"
              href="https://www.notion.so/replo/Feature-Flags-Guide-for-Support-b360ef51974b4ea2bb68a1e38a48f9f9?pvs=4"
            >
              Learn more
            </a>
          </span>
        </div>
        <Button
          size="sm"
          variant="primary"
          className="shrink-0"
          onClick={() => {
            clearFeatureFlagOverrides();
            rerender();
          }}
        >
          Reset to Defaults
        </Button>
      </div>
      <Input
        size="sm"
        placeholder="Search feature flags..."
        value={searchTerm}
        autoFocus
        onChange={(event) => setSearchTerm(event.target.value)}
        endEnhancer={<Search className="size-3.5 text-muted" />}
      />
      <div className="flex flex-col gap-1">
        {filteredSortedFlags.map((flag) => {
          const { value, description } = flag;
          return (
            <SelectionItem
              key={value}
              id={`feature-flag-${value}`}
              type="switch"
              variant="outline"
              label={value}
              description={description}
              checked={isFeatureEnabled(value)}
              UNSAFE_className="p-2 [&_[data-slot=field-label]]:text-xs [&_[data-slot=field-description]]:text-xs [&_[data-slot=field-description]]:[overflow-wrap:anywhere]"
              onCheckedChange={() => {
                if (isFeatureEnabled(value)) {
                  disableFeatureFlag(value);
                } else {
                  enableFeatureFlag(value);
                }
                rerender();
              }}
            />
          );
        })}
      </div>
    </div>
  );
};
